import { useState, useMemo, useRef, useEffect } from "react";
import { formatINR } from "../../utils/goldSilverParser";

const GOLD_COLOR = "#C9A227";
const SILVER_COLOR = "#9CA3AF";

function fmtDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d.getDate().toString().padStart(2,"0")} ${months[d.getMonth()]}`;
}

function cubicBezierPath(pts) {
  if (!pts.length) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const dx = (pts[i + 1].x - pts[i].x) / 3;
    d += ` C ${pts[i].x + dx} ${pts[i].y}, ${pts[i + 1].x - dx} ${pts[i + 1].y}, ${pts[i + 1].x} ${pts[i + 1].y}`;
  }
  return d;
}

export default function PriceChart({ priceData, isGold = true, is30=false }) {
  const [tooltip, setTooltip] = useState(null);
  const [containerW, setContainerW] = useState(800);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      setContainerW(entries[0].contentRect.width || 800);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const color = isGold ? GOLD_COLOR : SILVER_COLOR;
  const gradId = isGold ? "grad-gold" : "grad-silver";

  // Responsive layout values derived from actual pixel width
  const isMobile = containerW < 540;
  const W = containerW;
  const H = isMobile ? Math.round(W * 0.6) : Math.round(W * 0.28);
  const PAD = isMobile
    ? { l: 58, r: 10, t: 12, b: 26 }
    : { l: 72, r: 14, t: 14, b: 30 };
  const fontSize = isMobile ? 10 : 11;
  const dotR = isMobile ? 4 : 5;
  const strokeW = isMobile ? 2 : 2.5;
  const maxXLabels = isMobile ? 4 : 6;
  const CW = W - PAD.l - PAD.r;
  const CH = H - PAD.t - PAD.b;

  const chartData = useMemo(() => {
    const rates = priceData?.rates;
    if (!rates?.length) return [];
    return [...rates].reverse().map(r => ({
      date: r.date,
      value: isGold ? r.carat24 : r.tenGram,
      diff: r.difference,
      trend: r.trend,
    }));
  }, [priceData, isGold]);

  if (!chartData.length) return <p style={{ textAlign: "center", fontSize: "0.9em" }}>कोई डेटा उपलब्ध नहीं है</p>;

  const vals = chartData.map(d => d.value);
  const dataMin = Math.min(...vals);
  const dataMax = Math.max(...vals);
  const range = dataMax - dataMin || 1;

  // Nice Y-axis ticks
  const tickCount = isMobile ? 3 : 4;
  const rawStep = range / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const niceStep = Math.ceil(rawStep / magnitude) * magnitude;
  const yBottom = Math.floor(dataMin / magnitude) * magnitude;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => yBottom + i * niceStep);
  const yRange = yTicks[yTicks.length - 1] - yBottom;

  const toY = (v) => PAD.t + CH - ((v - yBottom) / yRange) * CH;
  const toX = (i) => PAD.l + (chartData.length > 1 ? (i / (chartData.length - 1)) * CW : CW / 2);

  const points = chartData.map((d, i) => ({ x: toX(i), y: toY(d.value), ...d }));
  const linePath = cubicBezierPath(points);
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${PAD.t + CH} L ${points[0].x} ${PAD.t + CH} Z`;

  // X-axis label indices
  const step = Math.max(1, Math.ceil(chartData.length / maxXLabels));
  const xLabelIdxs = new Set([0, chartData.length - 1]);
  for (let i = step; i < chartData.length - 1; i += step) xLabelIdxs.add(i);

  // Stats
  const oldest = chartData[0];
  const newest = chartData[chartData.length - 1];
  const overallTrend = priceData?.trend ?? (newest.value >= oldest.value ? "increase" : "decrease");
  const overallDiff = priceData?.difference ?? Math.abs(newest.value - oldest.value);
  const maxRate = priceData?.maxRate ?? dataMax;
  const minRate = priceData?.minRate ?? dataMin;
  const maxEntry = chartData.reduce((a, b) => b.value > a.value ? b : a, chartData[0]);
  const minEntry = chartData.reduce((a, b) => b.value < a.value ? b : a, chartData[0]);

  const statsBg = isGold ? "bg-amber-50" : "bg-gray-50";
  const statsBorder = isGold ? "border-amber-200" : "border-gray-200";

  const handlePointInteract = (p, i) => setTooltip({ ...p, i });

  return (
    <div className="w-full" ref={containerRef}>
      {/* Stats Bar */}
      <div className={`flex rounded-xl border ${statsBorder} ${statsBg} mb-3 overflow-hidden`}>
        <div className={`flex-1 px-2 py-2 sm:px-4 sm:py-3 border-r ${statsBorder}`}>
          <p className="text-[10px] sm:text-[11px] font-semibold text-gray-500 mb-0.5">
            {chartData.length} दिन के रेट का ट्रेंड
          </p>
          <p className={`text-sm sm:text-base font-bold flex items-center gap-0.5 ${overallTrend === "increase" ? "text-green-600" : "text-red-500"}`}>
            {overallTrend === "increase" ? "▲" : "▼"} {formatINR(overallDiff)}
          </p>
          <p className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5">
            {fmtDate(oldest.date)} – {fmtDate(newest.date)}
          </p>
        </div>
        <div className={`flex-1 px-2 py-2 sm:px-4 sm:py-3 border-r ${statsBorder}`}>
          <p className="text-[10px] sm:text-[11px] font-semibold text-gray-500 mb-0.5">{is30 ? isGold ? "इस महीने सोने के भाव का सबसे उच्च स्तर"  : "महीने का सबसे ऊंचा दाम" : !isGold ?"सबसे ज्यादा मूल्य" : "हाईएस्ट रेट"}</p>
          <p className="text-sm sm:text-base font-bold text-green-600">{formatINR(maxRate)}</p>
          <p className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5">{fmtDate(maxEntry.date)}</p>
        </div>
        <div className={`flex-1 px-2 py-2 sm:px-4 sm:py-3`}>
          <p className="text-[10px] sm:text-[11px] font-semibold text-gray-500 mb-0.5">{is30  ? isGold ? "इस महीने सोने के भाव का सबसे निम्न स्तर"  :"महीने का सबसे कम दाम"  : !isGold ?"सबसे कम मूल्य" : "लोएस्ट रेट"}</p>
          <p className="text-sm sm:text-base font-bold text-red-500">{formatINR(minRate)}</p>
          <p className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5">{fmtDate(minEntry.date)}</p>
        </div>
      </div>

      {/* Chart — SVG in real pixels so text never shrinks */}
      <div
        className="relative w-full"
        onMouseLeave={() => setTooltip(null)}
        onTouchEnd={() => setTooltip(null)}
      >
        {containerW > 0 && (
          <svg
            width={W}
            height={H}
            style={{ overflow: "visible", display: "block" }}
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.25" />
                <stop offset="100%" stopColor={color} stopOpacity="0.01" />
              </linearGradient>
            </defs>

            {/* Grid + Y-axis labels */}
            {yTicks.map((v, i) => (
              <g key={i}>
                <line
                  x1={PAD.l} y1={toY(v)} x2={W - PAD.r} y2={toY(v)}
                  stroke="#E5E7EB" strokeWidth="1"
                />
                <text
                  x={PAD.l - 14} y={toY(v) + 4}
                  textAnchor="end" fontSize={fontSize} fill="#9CA3AF"
                >
                  ₹{v.toLocaleString("en-IN")}
                </text>
              </g>
            ))}

            {/* Area fill */}
            <path d={areaPath} fill={`url(#${gradId})`} />

            {/* Line */}
            <path
              d={linePath} fill="none"
              stroke={color} strokeWidth={strokeW}
              strokeLinecap="round" strokeLinejoin="round"
            />

            {/* Dots — larger touch target via invisible overlay */}
            {points.map((p, i) => (
              <g key={i}>
                <circle
                  cx={p.x} cy={p.y} r={dotR}
                  fill={tooltip?.i === i ? color : "white"}
                  stroke={color} strokeWidth="2"
                />
                {/* Invisible hit area for touch */}
                <circle
                  cx={p.x} cy={p.y} r={dotR + 8}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => handlePointInteract(p, i)}
                  onTouchStart={(e) => { e.preventDefault(); handlePointInteract(p, i); }}
                />
              </g>
            ))}

            {/* X-axis labels */}
            {[...xLabelIdxs].map(i => (
              <text
                key={i}
                x={points[i].x} y={H - 4}
                textAnchor={i === 0 ? "start" : i === chartData.length - 1 ? "end" : "middle"}
                fontSize={fontSize} fill="#9CA3AF"
              >
                {fmtDate(chartData[i].date)}
              </text>
            ))}
          </svg>
        )}

        {/* Tooltip */}
        {tooltip && (
          <div
            className="absolute pointer-events-none z-10"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: "translate(-50%, calc(-100% - 12px))",
            }}
          >
            <div className="bg-gray-800 text-white text-xs rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
              <div className="font-semibold">{formatINR(tooltip.value)}</div>
              <div className="text-gray-300">{fmtDate(tooltip.date)}</div>
              {tooltip.diff != null && (
                <div className={tooltip.trend === "increase" ? "text-green-400" : "text-red-400"}>
                  {tooltip.trend === "increase" ? "▲" : "▼"} {formatINR(tooltip.diff)}
                </div>
              )}
            </div>
            <div className="w-2 h-2 bg-gray-800 rotate-45 mx-auto -mt-1" />
          </div>
        )}
      </div>
    </div>
  );
}
